import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {withAuthWrapperComponent} from "@/app/auth/WithAuthWrapperComponent";
import {companyApi} from "@/boxhub-api";
import {useNavigate, useParams} from "react-router-dom";
import {AppFrame} from "@/app/AppFrame";
import {useModelState} from "@/lib/modelstate";
import {Grid2 as Grid, useTheme} from "@mui/material";
import {useSnackbarEx} from "@/lib/snackbarex";
import {
    AddressDTO,
    CompanyActions,
    CompanyDTO,
    CompanyStatisticsDTO,
    CompanyStatus,
    UserDisplayDTO
} from "@/api/data-contracts";
import {IconButtonEx} from "@/lib/components/buttons/IconButtonEx";
import {
    ArchiveOutlined,
    Delete,
    Email,
    HighlightOffOutlined,
    Lock,
    LockOpen,
    RestartAltOutlined
} from "@mui/icons-material";
import {CompanyStatusBadge, getCompanyStatusText} from "@/common/CompanyStatusBadge";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {CardEx} from "@/lib/components/CardEx";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import SelectInputField from "@/lib/components/inputfields/select-input-field";
import {Constants} from "@/api/constants";
import {useCompanyEditSchema} from "@/pages/company-page/schema";
import {useLegalFormsByCountry} from "@/datacaches";
import {legalFormOptionsForDisplay, toSortedLegalFormOptions} from "@/model/legalForm";
import {AppPaths} from "@/app/AppPaths";
import {DataTable} from "@/lib/components/DataTable";
import {CellContext, ColumnDef} from "@tanstack/react-table";
import {UserStatusBadge} from "@/common/UserStatusBadge";
import {useTranslation} from "react-i18next";
import {AdminI18N, AuthI18N, CommonI18N, CompanyI18N, NotificationI18N, UserI18N} from "@/translations";
import {useDialogController} from "@/lib/dialog-controller";
import {UserInvitationDialog} from "@/pages/company-page/UserInvitationDialog";
import {truncateString} from "@/lib/utils";
import CheckboxInputField from "@/lib/components/inputfields/checkbox-input-field";
import {GroupBox} from "@/lib/components/GroupBox";
import {AddressEditDialog} from "@/pages/company-page/AddressEditDialog";
import ConfirmationDialog from "@/lib/components/ConfirmationDialog";
import {TerminationCompanyDialog} from "@/pages/company-page/TerminationCompanyDialog";
import DateInputField from "@/lib/components/inputfields/date-input-field";
import {getFullName} from "@/model/user";
import {BlockingCompanyDialog} from "@/pages/company-page/BlockingCompanyDialog";
import UndoIcon from "@mui/icons-material/Undo";
import EmailHistoryDialog from "@/common/EmailHistoryDialog";
import {isOperatorRole, useAuth} from "@/app/auth/AuthContext";
import {BriefIcon, PersonPlusIcon, SchlossIcon} from "@/agentur/AgenturIcons";
import {DeleteCompanyDialog} from "@/pages/company-page/DeleteCompanyDialog";
import {LinkDisplayDialog} from "@/common/dialogs/LinkDisplayDialog";
import {InvitationInfoCard} from "@/common/InvitationInfoCard";
import {MachineryParkLegend} from "@/pages/company-page/MachineryParkLegend";

type CompanyDetailsButtonsProps = {
    allowedActions: CompanyActions[] | undefined;
    locked: boolean;
    saving: boolean;
    isDirty: boolean;
    themeMode: string;
    showEmailButton: boolean;
    onResendInvitation: () => void;
    onGenerateSignupLink: () => void;
    onDelete: () => void;
    onInviteUser: () => void;
    onRejectSignup: () => void;
    onApproveSignup: () => void;
    onUnblock: () => void;
    onBlock: () => void;
    onRevertTermination: () => void;
    onReactivate: () => void;
    onArchive: () => void;
    onTerminate: () => void;
    onSave: () => void;
    onOpenEmailHistory: () => void;
    onToggleLock: () => void;
};

const CompanyDetailsButtons: React.FC<CompanyDetailsButtonsProps> = React.memo(
    ({
        allowedActions,
        locked,
        saving,
        isDirty,
        themeMode,
        showEmailButton,
        onResendInvitation,
        onGenerateSignupLink,
        onDelete,
        onInviteUser,
        onRejectSignup,
        onApproveSignup,
        onUnblock,
        onBlock,
        onRevertTermination,
        onReactivate,
        onArchive,
        onTerminate,
        onSave,
        onOpenEmailHistory,
        onToggleLock
    }) => {
        const { t } = useTranslation();
        return (
            <Grid container spacing={2} alignItems="center">
                {allowedActions?.includes(CompanyActions.RESEND_INVITATION) && (
                    <Grid>
                        <ButtonEx
                            label={t(AdminI18N.companies.action.resendInvitation)}
                            color="secondary"
                            startIcon={<Email />}
                            onClick={locked ? onResendInvitation : undefined}
                            disabled={!locked}
                        />
                    </Grid>
                )}
                {allowedActions?.includes(CompanyActions.RESEND_INVITATION) && (
                    <Grid>
                        <ButtonEx
                            label={t(AdminI18N.generateCompanySignupLink.action.generate)}
                            color="secondary"
                            onClick={locked ? onGenerateSignupLink : undefined}
                            disabled={!locked}
                        />
                    </Grid>
                )}
                {allowedActions?.includes(CompanyActions.DELETE_COMPANY) && (
                    <Grid>
                        <ButtonEx
                            label={t(AdminI18N.companies.action.delete)}
                            color="secondary"
                            startIcon={<Delete />}
                            onClick={locked ? onDelete : undefined}
                            disabled={!locked}
                        />
                    </Grid>
                )}
                {allowedActions?.includes(CompanyActions.ADD_USER) && (
                    <Grid>
                        <ButtonEx
                            label={t(UserI18N.form.action.inviteEmployee)}
                            color="secondary"
                            startIcon={<PersonPlusIcon mode={themeMode} />}
                            onClick={locked ? onInviteUser : undefined}
                            disabled={!locked}
                        />
                    </Grid>
                )}
                {allowedActions?.includes(CompanyActions.REJECT_COMPANY_REGISTRATION) && (
                    <Grid>
                        <ButtonEx
                            label={t(AdminI18N.companies.action.rejectSignupRequest)}
                            data-cy="reject-request-button"
                            onClick={onRejectSignup}
                            color="error"
                            disabled={saving || !locked}
                        />
                    </Grid>
                )}
                {allowedActions?.includes(CompanyActions.CONFIRM_COMPANY_REGISTRATION) && (
                    <Grid>
                        <ButtonEx
                            label={t(AdminI18N.companies.action.approveSignupRequest)}
                            data-cy="approve-request-button"
                            onClick={onApproveSignup}
                            color="primary"
                            disabled={saving || !locked}
                        />
                    </Grid>
                )}
                {allowedActions?.includes(CompanyActions.UNLOCK_COMPANY) && (
                    <Grid>
                        <ButtonEx
                            label={t(CompanyI18N.blocking.action.unblock)}
                            color="secondary"
                            startIcon={<SchlossIcon mode={themeMode} />}
                            onClick={locked ? onUnblock : undefined}
                            disabled={saving || !locked}
                        />
                    </Grid>
                )}
                {allowedActions?.includes(CompanyActions.LOCK_COMPANY) && (
                    <Grid>
                        <ButtonEx
                            label={t(CompanyI18N.blocking.action.block)}
                            color="error"
                            startIcon={<SchlossIcon mode={themeMode} />}
                            onClick={locked ? onBlock : undefined}
                            disabled={saving || !locked}
                        />
                    </Grid>
                )}
                {allowedActions?.includes(CompanyActions.CANCEL_COMPANY_TERMINATION) && (
                    <Grid>
                        <ButtonEx
                            label={t(CompanyI18N.termination.action.revert)}
                            color="secondary"
                            StartIcon={UndoIcon}
                            onClick={locked ? onRevertTermination : undefined}
                            disabled={saving || !locked}
                        />
                    </Grid>
                )}
                {allowedActions?.includes(CompanyActions.REACTIVATE_COMPANY) && (
                    <Grid>
                        <ButtonEx
                            label={t(CompanyI18N.reactivation.action.reactivate)}
                            color="secondary"
                            StartIcon={RestartAltOutlined}
                            onClick={locked ? onReactivate : undefined}
                            disabled={saving || !locked}
                        />
                    </Grid>
                )}
                {allowedActions?.includes(CompanyActions.ARCHIVE_COMPANY) && (
                    <Grid>
                        <ButtonEx
                            label={t(AdminI18N.companies.action.archive)}
                            color="error"
                            StartIcon={ArchiveOutlined}
                            onClick={locked ? onArchive : undefined}
                            disabled={saving || !locked}
                        />
                    </Grid>
                )}
                {allowedActions?.includes(CompanyActions.TERMINATE_COMPANY) && (
                    <Grid>
                        <ButtonEx
                            label={t(CompanyI18N.termination.action.terminate)}
                            color="error"
                            StartIcon={HighlightOffOutlined}
                            onClick={locked ? onTerminate : undefined}
                            disabled={saving || !locked}
                        />
                    </Grid>
                )}
                {!locked && (
                    <Grid>
                        <ButtonEx
                            label={t(AdminI18N.companies.action.saveChanges)}
                            data-cy="save-company-button"
                            onClick={onSave}
                            color="primary"
                            disabled={saving || !isDirty}
                        />
                    </Grid>
                )}
                <Grid>
                    <ButtonEx
                        label={t(NotificationI18N.emailHistory.action.open)}
                        startIcon={<BriefIcon mode={themeMode} />}
                        data-cy="email-history-button"
                        onClick={onOpenEmailHistory}
                        color="primary"
                        disabled={saving}
                        visible={showEmailButton}
                    />
                </Grid>
                <Grid>
                    <IconButtonEx
                        Icon={locked ? Lock : LockOpen}
                        tooltip={
                            locked
                                ? t(AdminI18N.companies.action.unlockForEditing)
                                : t(AdminI18N.companies.action.lockEditing)
                        }
                        onClick={onToggleLock}
                        disabled={saving}
                    />
                </Grid>
            </Grid>
        );
    }
);
CompanyDetailsButtons.displayName = "CompanyDetailsButtons";

const renderUserStatusBadgeCell = (info: CellContext<UserDisplayDTO, unknown>) => (
    <UserStatusBadge status={info.getValue() as UserDisplayDTO["status"]} />
);

const CompanyDetailsPage = () => {
    const { t } = useTranslation();
    const params = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const { showMessage } = useSnackbarEx();
    const [Anker, showDialog] = useDialogController();
    const { company, roles } = useAuth();
    const [locked, setLocked] = useState(true);

    const [companyStatistics, setCompanyStatistics] = useState<CompanyStatisticsDTO | undefined>(undefined);
    const [linkDialogData, setLinkDialogData] = useState<
        { title: string; url: string | undefined; validUntil?: string; subtitle?: string } | undefined
    >(undefined);

    const schema = useCompanyEditSchema();
    const [legalFormsByCountry] = useLegalFormsByCountry();

    const { model, errors, onChange, status, isDirty, dispatch, setErrors, save, load, httpErrorStatus } =
        useModelState<CompanyDTO, number>({
            schema,
            id: +params.id!,
            loader: companyApi.getCompany,
            updater: companyApi.saveCompany,
            validateDisabled: locked
        });

    // AK11: a legacy legal form no longer allowed for the company address's current country is still
    // appended as a transient out-of-list option here, so the plain-view select can render the stored
    // value — nothing is corrected or persisted until the operator explicitly saves a new value.
    const hasCompanyAddressCountry = !!model.companyAddress?.country;
    const isLegalFormDisabled = locked || !hasCompanyAddressCountry;
    const businessStructureSelectOptions = toSortedLegalFormOptions(
        legalFormOptionsForDisplay(model.companyAddress?.country, model.legalForm, legalFormsByCountry),
        t
    );

    const loadCompanyStatistics = useCallback(async () => {
        // Firmenstatistik ist eine Operator-Funktion (Backend: @PreAuthorize(HAS_OPERATOR_ROLE)).
        // Reguläre Firmennutzer (CLIENT / CLIENT_MANAGING_DIRECTOR) dürfen den Endpoint nicht aufrufen
        // und würden sonst ein 403 erhalten – daher gar nicht erst anfragen.
        if (!model.id || !isOperatorRole(roles)) {
            return;
        }
        try {
            const response = await companyApi.getCompanyStatistics(model.id);
            setCompanyStatistics(response.data);
        } catch (error: unknown) {
            showMessage({ summary: t(CompanyI18N.form.message.loadStatisticsFailed), error, severity: "error" });
        }
    }, [model.id, roles, showMessage, t]);

    useEffect(() => {
        loadCompanyStatistics();
    }, [model.id, loadCompanyStatistics]);

    const employeesColumns = useMemo<ColumnDef<UserDisplayDTO>[]>(
        () => [
            {
                header: t(UserI18N.form.field.salutation),
                accessorKey: "salutation",
                accessorFn: (row) => row.salutation && t(CommonI18N.enum.salutation[row.salutation])
            },
            {
                header: t(UserI18N.form.field.firstName),
                accessorKey: "firstName"
            },
            {
                header: t(UserI18N.form.field.lastName),
                accessorKey: "lastName"
            },
            {
                header: t(UserI18N.form.field.email),
                accessorKey: "email"
            },
            {
                header: t(UserI18N.form.field.phone),
                accessorKey: "telephone"
            },
            {
                header: t(AdminI18N.companies.column.isCEO),
                accessorKey: "isCEO",
                accessorFn: (row) =>
                    t(
                        row?.id != null && row.id === model.contactPerson?.id
                            ? CommonI18N.message.yes
                            : CommonI18N.message.no
                    )
            },
            {
                header: t(AdminI18N.companies.column.userStatus),
                accessorKey: "status",
                cell: renderUserStatusBadgeCell
            }
        ],
        [t, model.contactPerson?.id]
    );

    const companyDetailsColumns = useMemo<ColumnDef<AddressDTO>[]>(
        () => [
            {
                header: t(CompanyI18N.form.field.street),
                accessorFn: (row) => [row.street, row.houseNumber].filter(Boolean).join(" ")
            },
            { header: t(CompanyI18N.form.field.postalCode), accessorFn: (row) => row.zip },
            { header: t(CompanyI18N.form.field.city), accessorFn: (row) => row.city },
            {
                header: t(CompanyI18N.form.field.country),
                accessorFn: (row) => (row.country ? t(CommonI18N.enum.country[row.country]) : "")
            },
            {
                header: t(CompanyI18N.form.field.isHeadquarter),
                accessorFn: (row) => t(row.companyAddress ? CommonI18N.message.yes : CommonI18N.message.no)
            },
            {
                header: t(CompanyI18N.form.field.isTimeSlotBooking),
                accessorFn: (row) =>
                    t(row.deliveryInstruction?.timeSlotBookingRequired ? CommonI18N.message.yes : CommonI18N.message.no)
            }
        ],
        [t]
    );

    const navigateToCompanyOverview = useCallback(() => {
        navigate(AppPaths.COMPANY_UNVERIFIED, { replace: true });
    }, [navigate]);

    const doSaveCompany = useCallback(async () => {
        const result = save();
        if (result) {
            await result;
            setLocked(true);
        }
    }, [save]);

    const approveSignUpRequest = useCallback(async () => {
        if (model.id === undefined) {
            showMessage({ summary: t(AdminI18N.companies.message.approveFailed), severity: "error" });
            return;
        }
        if (isDirty) {
            dispatch({ type: "--reset" });
        }
        try {
            await companyApi.approveRegistration(model.id);
            showMessage({ summary: t(AdminI18N.companies.message.approveSuccess), severity: "success" });
            navigateToCompanyOverview();
        } catch (error: unknown) {
            showMessage({ summary: t(AdminI18N.companies.message.approveFailed), error, severity: "error" });
        }
    }, [model.id, isDirty, dispatch, showMessage, t, navigateToCompanyOverview]);

    const rejectSignUpRequest = useCallback(async () => {
        if (model.id === undefined) {
            showMessage({ summary: t(AdminI18N.companies.message.rejectFailed), severity: "error" });
            return;
        }
        if (isDirty) {
            dispatch({ type: "--reset" });
        }
        try {
            await companyApi.rejectRegistration(model.id);
            showMessage({ summary: t(AdminI18N.companies.message.rejectSuccess), severity: "success" });
            navigateToCompanyOverview();
        } catch (error: unknown) {
            showMessage({ summary: t(AdminI18N.companies.message.rejectFailed), error, severity: "error" });
        }
    }, [model.id, isDirty, dispatch, showMessage, t, navigateToCompanyOverview]);

    const handleOpenEmailHistoryDialog = useCallback(() => {
        showDialog((open, close) => <EmailHistoryDialog open={open} onClose={close} />);
    }, [showDialog]);

    const handleLockToggle = useCallback(() => {
        if (locked) {
            setLocked(false);
            return;
        }
        if (!isDirty) {
            setLocked(true);
            setErrors({});
            return;
        }
        const handleConfirmLock = (close: () => void) => {
            dispatch({ type: "--reset" });
            setLocked(true);
            setErrors({});
            close();
        };
        showDialog((open, close) => (
            <ConfirmationDialog
                open={open}
                onClose={close}
                onConfirm={() => handleConfirmLock(close)}
                title={t(CompanyI18N.form.dialog.lockTitle)}
                message={t(CompanyI18N.form.dialog.lockMessage)}
                confirmButtonText={t(CommonI18N.message.yes)}
                cancelButtonText={t(CommonI18N.message.no)}
            />
        ));
    }, [locked, isDirty, dispatch, setErrors, showDialog, t]);

    const handleDeleteCompany = useCallback(() => {
        showDialog((open, close) => (
            <DeleteCompanyDialog open={open} close={close} companyId={model.id!} companyName={model.name!} />
        ));
    }, [showDialog, model.id, model.name]);

    const handleInviteUser = useCallback(() => {
        showDialog((open, close) => (
            <UserInvitationDialog open={open} close={close} onSuccess={() => load()} companyId={model.id!} />
        ));
    }, [showDialog, load, model.id]);

    const handleTerminationCompany = useCallback(() => {
        showDialog((open, close) => (
            <TerminationCompanyDialog open={open} close={close} company={model} onSuccess={() => load()} />
        ));
    }, [showDialog, model, load]);

    const handleResendInvitation = useCallback(async () => {
        if (model.id === undefined) {
            return;
        }
        try {
            const response = await companyApi.resendCompanyInvitation(model.id);
            const token = response.data;
            const link = `${globalThis.location.origin}#${AppPaths.COMPANY_SIGNUP_fn(token)}`;
            setLinkDialogData({
                title: t(AdminI18N.linkDisplay.dialog.companySignupTitle),
                url: link,
                validUntil: undefined,
                subtitle: t(AdminI18N.companies.message.resendInvitationSuccess)
            });
            load();
        } catch (error: unknown) {
            showMessage({
                summary: t(AdminI18N.companies.message.resendInvitationFailed),
                error,
                severity: "error"
            });
        }
    }, [model.id, t, showMessage, load]);

    const handleGenerateCompanySignupLink = useCallback(() => {
        const onConfirmGenerateLink = async (close: () => void) => {
            close();
            if (model.id === undefined) {
                showMessage({
                    summary: t(AdminI18N.generateCompanySignupLink.message.errorNotFound),
                    severity: "error"
                });
                return;
            }
            try {
                const response = await companyApi.generateSignupLink(model.id);
                const { url, validUntil } = response.data;
                setLinkDialogData({
                    title: t(AdminI18N.linkDisplay.dialog.companySignupTitle),
                    url,
                    validUntil: validUntil ?? undefined,
                    subtitle: t(AdminI18N.linkDisplay.message.warning)
                });
                load();
            } catch (error: unknown) {
                const axiosError = error as { response?: { status?: number } };
                const status = axiosError?.response?.status;
                const summaryKey =
                    status === 404 || status === 409
                        ? AdminI18N.generateCompanySignupLink.message.errorNotFound
                        : AdminI18N.generateCompanySignupLink.message.errorConflict;
                showMessage({ summary: t(summaryKey), error: status ? undefined : error, severity: "error" });
            }
        };
        showDialog((open, close) => (
            <ConfirmationDialog
                open={open}
                onClose={close}
                onConfirm={() => onConfirmGenerateLink(close)}
                title={t(AdminI18N.generateCompanySignupLink.dialog.confirmTitle)}
                message={t(AdminI18N.generateCompanySignupLink.dialog.confirmMessage)}
                confirmButtonText={t(CommonI18N.message.yes)}
                cancelButtonText={t(CommonI18N.message.no)}
            />
        ));
    }, [model.id, showMessage, showDialog, t, load]);

    const handleRevertTerminationCompany = useCallback(() => {
        const onConfirmRevertTermination = async (close: () => void) => {
            if (model.id === undefined) {
                showMessage({ summary: t(CompanyI18N.termination.message.revertFailed), severity: "error" });
                return;
            }
            try {
                await companyApi.cancelTerminationCompany(model.id);
                showMessage({ summary: t(CompanyI18N.termination.message.revertSuccess), severity: "success" });
                close();
                load();
            } catch (error: unknown) {
                showMessage({ summary: t(CompanyI18N.termination.message.revertFailed), error, severity: "error" });
            }
        };
        showDialog((open, close) => (
            <ConfirmationDialog
                open={open}
                onClose={close}
                onConfirm={() => onConfirmRevertTermination(close)}
                title={t(CompanyI18N.termination.dialog.revertConfirmTitle, { companyName: model.name })}
                message={t(CompanyI18N.termination.dialog.revertConfirmMessage, { companyName: model.name })}
                confirmButtonText={t(CommonI18N.message.yes)}
                cancelButtonText={t(CommonI18N.message.no)}
            />
        ));
    }, [model.id, model.name, showMessage, showDialog, t, load]);

    const handleReactivationCompany = useCallback(() => {
        const onConfirmReactivation = async (close: () => void) => {
            if (model.id === undefined) {
                showMessage({ summary: t(CompanyI18N.reactivation.message.failed), severity: "error" });
                return;
            }
            try {
                await companyApi.reactivateCompany(model.id);
                showMessage({ summary: t(CompanyI18N.reactivation.message.success), severity: "success" });
                close();
                load();
            } catch (error: unknown) {
                showMessage({ summary: t(CompanyI18N.reactivation.message.failed), error, severity: "error" });
            }
        };
        showDialog((open, close) => (
            <ConfirmationDialog
                open={open}
                onClose={close}
                onConfirm={() => onConfirmReactivation(close)}
                title={t(CompanyI18N.reactivation.dialog.confirmTitle, { companyName: model.name })}
                message={t(CompanyI18N.reactivation.dialog.confirmMessage, { companyName: model.name })}
                confirmButtonText={t(CommonI18N.message.yes)}
                cancelButtonText={t(CommonI18N.message.no)}
            />
        ));
    }, [model.id, model.name, showMessage, showDialog, t, load]);

    const handleArchiveCompany = useCallback(() => {
        const onConfirmArchive = async (close: () => void) => {
            if (model.id === undefined) {
                showMessage({ summary: t(AdminI18N.companies.message.archiveFailed), severity: "error" });
                return;
            }
            try {
                await companyApi.archiveCompany(model.id);
                showMessage({ summary: t(AdminI18N.companies.message.archiveSuccess), severity: "success" });
                close();
                load();
            } catch (error: unknown) {
                showMessage({ summary: t(AdminI18N.companies.message.archiveFailed), error, severity: "error" });
            }
        };
        showDialog((open, close) => (
            <ConfirmationDialog
                open={open}
                onClose={close}
                onConfirm={() => onConfirmArchive(close)}
                title={t(AdminI18N.companies.dialog.archiveTitle, { companyName: model.name })}
                message={t(AdminI18N.companies.dialog.archiveMessage)}
                confirmButtonText={t(CommonI18N.message.yes)}
                cancelButtonText={t(CommonI18N.message.no)}
            />
        ));
    }, [model.id, model.name, showMessage, showDialog, t, load]);

    const handleBlockingCompany = useCallback(() => {
        showDialog((open, close) => (
            <BlockingCompanyDialog
                open={open}
                close={close}
                company={model}
                companyStatistics={companyStatistics}
                onSuccess={() => load()}
            />
        ));
    }, [showDialog, model, companyStatistics, load]);

    const handleUnblockingCompany = useCallback(() => {
        const onConfirmUnblocking = async (close: () => void) => {
            if (model.id === undefined) {
                showMessage({ summary: t(CompanyI18N.blocking.message.revertFailed), severity: "error" });
                return;
            }
            try {
                await companyApi.unlockCompany(model.id);
                showMessage({ summary: t(CompanyI18N.blocking.message.revertSuccess), severity: "success" });
                close();
                load();
            } catch (error: unknown) {
                showMessage({ summary: t(CompanyI18N.blocking.message.revertFailed), error, severity: "error" });
            }
        };
        showDialog((open, close) => (
            <ConfirmationDialog
                open={open}
                onClose={close}
                onConfirm={() => onConfirmUnblocking(close)}
                title={t(CompanyI18N.blocking.dialog.revertConfirmTitle, { companyName: model.name })}
                message={t(CompanyI18N.blocking.dialog.revertConfirmMessage, { companyName: model.name })}
                confirmButtonText={t(CommonI18N.message.yes)}
                cancelButtonText={t(CommonI18N.message.no)}
            />
        ));
    }, [model.id, model.name, showMessage, showDialog, t, load]);

    const pageTitle = (
        <>
            {`${t(AdminI18N.companies.label.company)} '${truncateString(model?.name, 64)}' `}
            &nbsp;
            <CompanyStatusBadge status={model?.status} />
        </>
    );

    const buttons = (
        <CompanyDetailsButtons
            allowedActions={model?.allowedActions}
            locked={locked}
            saving={status === "saving"}
            isDirty={isDirty}
            themeMode={theme.palette.mode}
            showEmailButton={!!(model?.id && model?.id === company?.id)}
            onResendInvitation={handleResendInvitation}
            onGenerateSignupLink={handleGenerateCompanySignupLink}
            onDelete={handleDeleteCompany}
            onInviteUser={handleInviteUser}
            onRejectSignup={rejectSignUpRequest}
            onApproveSignup={approveSignUpRequest}
            onUnblock={handleUnblockingCompany}
            onBlock={handleBlockingCompany}
            onRevertTermination={handleRevertTerminationCompany}
            onReactivate={handleReactivationCompany}
            onArchive={handleArchiveCompany}
            onTerminate={handleTerminationCompany}
            onSave={doSaveCompany}
            onOpenEmailHistory={handleOpenEmailHistoryDialog}
            onToggleLock={handleLockToggle}
        />
    );

    const companyForm = (
        <CardEx title={t(CompanyI18N.form.page.title)}>
            <Grid container direction="row" spacing={2}>
                <Grid size={{ xs: 12 }}>
                    <StringInputField
                        name="name"
                        disabled={locked}
                        value={model?.name}
                        label={t(CompanyI18N.form.field.name)}
                        error={errors?.name}
                        maxChars={Constants.COMPANY_NAME_LENGTH_MAX}
                        onChange={onChange}
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <SelectInputField
                        name="legalForm"
                        disabled={isLegalFormDisabled}
                        label={t(CompanyI18N.form.field.legalForm)}
                        value={model?.legalForm}
                        // While there is no company-address country yet, the required-legalForm error
                        // must not win over the "choose a country first" hint (analog Phase 2.2 fix).
                        error={hasCompanyAddressCountry ? errors?.legalForm : undefined}
                        options={businessStructureSelectOptions}
                        displayEmpty={true}
                        helperText={
                            !locked && !hasCompanyAddressCountry
                                ? t(CompanyI18N.form.field.legalFormCountryHint)
                                : undefined
                        }
                        onChange={onChange}
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <StringInputField
                        name="website"
                        disabled={locked}
                        value={model?.website}
                        label={t(CompanyI18N.form.field.website)}
                        error={errors?.website}
                        maxChars={Constants.WEBSITE_LENGTH_MAX}
                        onChange={onChange}
                        activatePrefix={true}
                        prefixValue="https://"
                    />
                </Grid>
                <Grid size={{ xs: 6 }}>
                    <StringInputField
                        name="commercialRegistrationNumber"
                        disabled={locked}
                        value={model?.commercialRegistrationNumber}
                        label={t(CompanyI18N.form.field.commercialRegisterNumber)}
                        error={errors?.commercialRegistrationNumber}
                        maxChars={Constants.COMMERCIAL_REGISTER_NUMBER_LENGTH_MAX}
                        onChange={onChange}
                    />
                </Grid>
                <Grid size={{ xs: 6 }}>
                    <StringInputField
                        name="vatIdentificationNumber"
                        disabled={locked}
                        value={model?.vatIdentificationNumber}
                        label={t(CompanyI18N.form.field.vatId)}
                        error={errors?.vatIdentificationNumber}
                        maxChars={Constants.VAT_ID_LENGTH_MAX}
                        onChange={onChange}
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <StringInputField
                        name="officeHours"
                        disabled={locked}
                        value={model?.officeHours}
                        label={t(CompanyI18N.form.field.officeHours)}
                        error={errors?.officeHours}
                        maxChars={Constants.OFFICE_HOURS_LENGTH_MAX}
                        onChange={onChange}
                        multiline={true}
                    />
                </Grid>
            </Grid>
        </CardEx>
    );

    const bankForm = (
        <CardEx title={t(CompanyI18N.form.page.bankConnectionTitle)}>
            <Grid container direction="row" spacing={2} width="100%">
                <Grid size={{ xs: 6 }}>
                    <StringInputField
                        name="bankName"
                        disabled={locked}
                        value={model?.bankName}
                        label={t(CompanyI18N.form.field.bankName)}
                        error={errors?.bankName}
                        maxChars={Constants.BANKNAME_LENGTH_MAX}
                        onChange={onChange}
                        multiline={true}
                    />
                </Grid>
                <Grid size={{ xs: 6 }}>
                    <StringInputField
                        name="iban"
                        disabled={locked}
                        value={model?.iban}
                        label={t(CompanyI18N.form.field.iban)}
                        error={errors?.iban}
                        maxChars={Constants.IBAN_LENGTH_MAX}
                        onChange={onChange}
                        multiline={true}
                    />
                </Grid>
                <Grid size={{ xs: 6 }}>
                    <StringInputField
                        name="bic"
                        disabled={locked}
                        value={model?.bic}
                        label={t(CompanyI18N.form.field.bic)}
                        error={errors?.bic}
                        maxChars={Constants.BIC_LENGTH_MAX}
                        onChange={onChange}
                        multiline={true}
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <StringInputField
                        name="paymentTerms"
                        disabled={locked}
                        value={model?.paymentTerms}
                        label={t(CompanyI18N.form.field.paymentTerms)}
                        error={errors?.paymentTerms}
                        maxChars={Constants.PAYMENT_TERMS_LENGTH_MAX}
                        onChange={onChange}
                        multiline={true}
                    />
                </Grid>
            </Grid>
        </CardEx>
    );

    const erpSystemForm = (
        <CardEx title={t(CompanyI18N.form.page.erpSystemTitle)}>
            <Grid container spacing={2} direction="row" width="100%">
                <Grid size={{ xs: 12 }}>
                    <StringInputField
                        name="erpManufacturer"
                        disabled={locked}
                        value={model?.erpManufacturer}
                        label={t(CompanyI18N.form.field.erpManufacturer)}
                        error={errors?.erpManufacturer}
                        maxChars={Constants.ERP_MANUFACTURER_LENGTH_MAX}
                        onChange={onChange}
                        multiline={true}
                    />
                </Grid>
                <Grid size={{ xs: 6 }}>
                    <StringInputField
                        name="erpSoftwareName"
                        disabled={locked}
                        value={model?.erpSoftwareName}
                        label={t(CompanyI18N.form.field.erpSoftwareName)}
                        error={errors?.erpSoftwareName}
                        maxChars={Constants.ERP_SOFTWARE_NAME_LENGTH_MAX}
                        onChange={onChange}
                        multiline={true}
                    />
                </Grid>
                <Grid size={{ xs: 6 }}>
                    <StringInputField
                        name="erpSoftwareVersion"
                        disabled={locked}
                        value={model?.erpSoftwareVersion}
                        label={t(CompanyI18N.form.field.erpSoftwareVersion)}
                        error={errors?.erpSoftwareVersion}
                        maxChars={Constants.ERP_SOFTWARE_VERSION_LENGTH_MAX}
                        onChange={onChange}
                        multiline={true}
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <StringInputField
                        name="additionalInformation"
                        disabled={locked}
                        value={model?.additionalInformation}
                        label={t(CompanyI18N.form.field.additionalInformation)}
                        error={errors?.additionalInformation}
                        maxChars={Constants.ADDITIONAL_INFORMATION_LENGTH_MAX}
                        onChange={onChange}
                        multiline={true}
                    />
                </Grid>
            </Grid>
        </CardEx>
    );

    const machineryParkForm = (
        <CardEx title={t(CompanyI18N.form.field.machineryPark)}>
            <GroupBox title={t(CompanyI18N.form.field.machineryPark)} disabled={locked}>
                <Grid container spacing={0}>
                    <Grid size={{ sm: 6, md: 5, lg: 4 }}>
                        <CheckboxInputField
                            name="maschineryFleet.miniliner"
                            disabled={locked}
                            label={t(CompanyI18N.form.field.miniliner)}
                            onChange={onChange}
                            value={model?.maschineryFleet?.miniliner}
                            error={errors?.maschineryFleet?.miniliner}
                            dense
                        />
                    </Grid>
                    <Grid size={{ sm: 6, md: 5, lg: 4 }}>
                        <CheckboxInputField
                            name="maschineryFleet.midliner"
                            disabled={locked}
                            label={t(CompanyI18N.form.field.midliner)}
                            onChange={onChange}
                            value={model?.maschineryFleet?.midliner}
                            error={errors?.maschineryFleet?.midliner}
                            dense
                        />
                    </Grid>
                    <Grid size={{ sm: 6, md: 5, lg: 4 }}>
                        <CheckboxInputField
                            name="maschineryFleet.maxiliner"
                            disabled={locked}
                            label={t(CompanyI18N.form.field.maxiliner)}
                            onChange={onChange}
                            value={model?.maschineryFleet?.maxiliner}
                            error={errors?.maschineryFleet?.maxiliner}
                            dense
                        />
                    </Grid>
                    <Grid size={{ sm: 6, md: 5, lg: 4 }}>
                        <CheckboxInputField
                            name="maschineryFleet.dieCutting"
                            disabled={locked}
                            label={t(CompanyI18N.form.field.dieCutting)}
                            onChange={onChange}
                            value={model?.maschineryFleet?.dieCutting}
                            error={errors?.maschineryFleet?.dieCutting}
                            dense
                        />
                    </Grid>
                    <Grid size={{ sm: 6, md: 5, lg: 4 }}>
                        <CheckboxInputField
                            name="maschineryFleet.folderGluerMachine"
                            disabled={locked}
                            label={t(CompanyI18N.form.field.folderGluerMachine)}
                            onChange={onChange}
                            value={model?.maschineryFleet?.folderGluerMachine}
                            error={errors?.maschineryFleet?.folderGluerMachine}
                            dense
                        />
                    </Grid>
                    <Grid size={{ sm: 6, md: 5, lg: 4 }}>
                        <CheckboxInputField
                            name="maschineryFleet.packaging"
                            disabled={locked}
                            label={t(CompanyI18N.form.field.packaging)}
                            onChange={onChange}
                            value={model?.maschineryFleet?.packaging}
                            error={errors?.maschineryFleet?.packaging}
                            dense
                        />
                    </Grid>
                    <Grid size={{ sm: 6, md: 5, lg: 4 }}>
                        <CheckboxInputField
                            name="maschineryFleet.hazardousGoodsQualification"
                            disabled={locked}
                            label={t(CompanyI18N.form.field.hazardousGoodsQualification)}
                            onChange={onChange}
                            value={model?.maschineryFleet?.hazardousGoodsQualification}
                            error={errors?.maschineryFleet?.hazardousGoodsQualification}
                            dense
                        />
                    </Grid>
                    <Grid size={{ sm: 6, md: 5, lg: 4 }}>
                        <CheckboxInputField
                            name="maschineryFleet.flexoPrinting"
                            disabled={locked}
                            label={t(CompanyI18N.form.field.flexoPrinting)}
                            onChange={onChange}
                            value={model?.maschineryFleet?.flexoPrinting}
                            error={errors?.maschineryFleet?.flexoPrinting}
                            dense
                        />
                    </Grid>
                    <Grid size={{ sm: 6, md: 5, lg: 4 }}>
                        <CheckboxInputField
                            name="maschineryFleet.hdFlexoPrinting"
                            disabled={locked}
                            label={t(CompanyI18N.form.field.hdFlexoPrinting)}
                            onChange={onChange}
                            value={model?.maschineryFleet?.hdFlexoPrinting}
                            error={errors?.maschineryFleet?.hdFlexoPrinting}
                            dense
                        />
                    </Grid>
                    <Grid size={{ sm: 6, md: 5, lg: 4 }}>
                        <CheckboxInputField
                            name="maschineryFleet.offsetPrinting"
                            disabled={locked}
                            label={t(CompanyI18N.form.field.offsetPrinting)}
                            onChange={onChange}
                            value={model?.maschineryFleet?.offsetPrinting}
                            error={errors?.maschineryFleet?.offsetPrinting}
                            dense
                        />
                    </Grid>
                    <Grid size={{ sm: 6, md: 5, lg: 4 }}>
                        <CheckboxInputField
                            name="maschineryFleet.digitalPrinting"
                            disabled={locked}
                            label={t(CompanyI18N.form.field.digitalPrinting)}
                            onChange={onChange}
                            value={model?.maschineryFleet?.digitalPrinting}
                            error={errors?.maschineryFleet?.digitalPrinting}
                            dense
                        />
                    </Grid>
                </Grid>
            </GroupBox>
            {locked && <MachineryParkLegend />}
        </CardEx>
    );

    const addressTable = (
        <CardEx title={t(AuthI18N.login.page.addressDataTitle)}>
            <DataTable
                columns={companyDetailsColumns}
                data={model.companyAddress ? [model.companyAddress] : []}
                noResultsValue={t(AdminI18N.companies.message.noEmployeesFound)}
                striped
                onRowClick={
                    locked
                        ? (row) => {
                              showDialog((open, close) => (
                                  <AddressEditDialog
                                      open={open}
                                      close={close}
                                      onSuccess={() => load()}
                                      address={row}
                                      companyId={model.id}
                                      currentLegalForm={model.legalForm}
                                  />
                              ));
                          }
                        : undefined
                }
            />
        </CardEx>
    );

    const userTable = (
        <CardEx title={t(AdminI18N.companies.page.usersTitle)}>
            <DataTable
                columns={employeesColumns}
                data={model.users ?? []}
                noResultsValue={t(AdminI18N.companies.message.noEmployeesFound)}
                striped
                onRowClick={(row) => {
                    navigate(AppPaths.USER_fn(row.id!, model.id));
                }}
            />
        </CardEx>
    );

    const readOnlyTermination = (
        <CardEx title={t(CompanyI18N.termination.page.cardTitle)}>
            <Grid container spacing={2} width="100%">
                <Grid container spacing={2} width="100%">
                    <Grid container spacing={2} width="100%">
                        <Grid size={{ xs: 6 }}>
                            <StringInputField
                                value={model.status ? getCompanyStatusText(t, model.status) : ""}
                                label={t(CompanyI18N.termination.field.status)}
                                disabled
                            />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <DateInputField
                                name="termination.requestExecDate"
                                disabled
                                value={model?.termination?.requestExecDate}
                                label={t(CompanyI18N.termination.field.date)}
                            />
                        </Grid>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <StringInputField
                            value={model.termination?.requestReason}
                            label={t(CompanyI18N.termination.field.reason)}
                            multiline
                            disabled
                        />
                    </Grid>
                    <Grid container spacing={2} width="100%">
                        <Grid size={{ xs: 6 }}>
                            <StringInputField
                                value={
                                    model.termination?.requestSource
                                        ? t(CommonI18N.enum.terminationInitiator[model.termination?.requestSource])
                                        : ""
                                }
                                label={t(CompanyI18N.termination.field.initiator)}
                                disabled
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} width="100%">
                        <Grid size={{ xs: 6 }}>
                            <DateInputField
                                name="termination.requestAt"
                                disabled
                                value={model?.termination?.requestAt}
                                label={t(CompanyI18N.termination.field.requestedAt)}
                            />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <StringInputField
                                value={getFullName(model.termination?.requestBy)}
                                label={t(CompanyI18N.termination.field.requestedBy)}
                                disabled
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </CardEx>
    );

    const readOnlyBlocking = (
        <CardEx title={t(CompanyI18N.blocking.page.cardTitle)}>
            <Grid container spacing={2} width="100%">
                <Grid container spacing={2} width="100%">
                    <Grid size={{ xs: 6 }}>
                        <StringInputField
                            value={model.lock ? t(CompanyI18N.blocking.field.locked) : ""}
                            label={t(CompanyI18N.blocking.field.status)}
                            disabled
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <StringInputField
                            value={model.lock?.lockedReason}
                            label={t(CompanyI18N.blocking.field.reason)}
                            multiline
                            disabled
                        />
                    </Grid>
                    <Grid container spacing={2} width="100%">
                        <Grid size={{ xs: 6 }}>
                            <DateInputField
                                name="lock.lockedAt"
                                disabled
                                value={model?.lock?.lockedAt}
                                label={t(CompanyI18N.blocking.field.requestedAt)}
                            />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <StringInputField
                                value={getFullName(model.lock?.lockedBy)}
                                label={t(CompanyI18N.blocking.field.requestedBy)}
                                disabled
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </CardEx>
    );

    return (
        <AppFrame
            modelState={status}
            httpErrorStatus={httpErrorStatus}
            title={pageTitle}
            buttons={buttons}
            isDirty={isDirty}
        >
            <Grid container direction="row" spacing={2} paddingTop={1} width="100%">
                <Grid container direction="column" spacing={2} size={{ md: 12, lg: 5 }}>
                    <Grid size={{ xs: 12 }}>{companyForm}</Grid>
                    <Grid size={{ xs: 12 }}> {bankForm}</Grid>
                    <Grid size={{ xs: 12 }}>{erpSystemForm}</Grid>
                </Grid>
                <Grid container direction="column" spacing={2} size={{ md: 12, lg: 7 }}>
                    <Grid size={{ xs: 12 }}>{machineryParkForm}</Grid>
                    <Grid size={{ xs: 12 }}>{addressTable}</Grid>
                    <Grid size={{ xs: 12 }}>{userTable}</Grid>
                    {(model.status === CompanyStatus.TERMINATED ||
                        model.status === CompanyStatus.TERMINATION_RECEIVED ||
                        model.status === CompanyStatus.TERMINATION_RECEIVED_LOCKED) && (
                        <Grid size={{ xs: 12 }}>{readOnlyTermination}</Grid>
                    )}
                    {(model.status === CompanyStatus.CONFIRMED_LOCKED ||
                        model.status === CompanyStatus.TERMINATION_RECEIVED_LOCKED) && (
                        <Grid size={{ xs: 12 }}>{readOnlyBlocking}</Grid>
                    )}
                    {model.status === CompanyStatus.INVITED && isOperatorRole(roles) && !!model.invitation && (
                        <Grid size={{ xs: 12 }}>
                            <InvitationInfoCard invitation={model.invitation} />
                        </Grid>
                    )}
                </Grid>
            </Grid>
            <Anker />
            <LinkDisplayDialog
                open={linkDialogData !== undefined}
                onClose={() => setLinkDialogData(undefined)}
                title={linkDialogData?.title ?? ""}
                link={linkDialogData?.url}
                validUntil={linkDialogData?.validUntil}
                subtitle={linkDialogData?.subtitle}
            />
        </AppFrame>
    );
};

export default withAuthWrapperComponent(CompanyDetailsPage);
